import { ButtonPrimary } from 'app/components/Base/ButtonPrimary'
import { InputFile } from 'app/components/Base/InputFile'
import { uploadCsvToS3 } from 'app/services/pharmacyDataUpdate'
import { useCallback, useMemo, useRef, useState } from 'react'
import { PharmacyDataUpdateUrl } from 'types/PharmacyDataUpdateUrl'
import { DATASET, Dataset } from '../constants/dataset'
import { DATASET_OPTIONS } from '../constants/datasetOptions'
import { LastUploadedInfo } from '../types/LastUploadedInfo'
import { FetchDropdown, FetchDropdownHandle } from './FetchDropdown'

const UPLOAD_STATUS = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
} as const
type UploadStatus = (typeof UPLOAD_STATUS)[keyof typeof UPLOAD_STATUS]

export type PresignedUrlInfo = PharmacyDataUpdateUrl & {
  expiresCallback: () => void
}

const DATASET_VALUES = Object.values(DATASET) as Dataset[]
const isDataset = (v: unknown): v is Dataset =>
  DATASET_VALUES.includes(v as Dataset)

type FileUploaderProps = {
  setUploadedInfo: (name: string, dataset: Dataset, uploadToken: string) => void
}

export function FileUploader({ setUploadedInfo }: FileUploaderProps) {
  const fetchDropdownRef = useRef<FetchDropdownHandle>(null)
  const [presignedUrlInfo, setPresignedUrlInfo] =
    useState<PresignedUrlInfo | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [isFileChanged, setIsFileChanged] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>(
    UPLOAD_STATUS.IDLE,
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [lastUploadedInfo, setLastUploadedInfo] =
    useState<LastUploadedInfo | null>(null)

  const handleSetPresignedUrl = useCallback(
    (info: PresignedUrlInfo | null) => {
      setPresignedUrlInfo(info)
      if (info) {
        if (uploadStatus === UPLOAD_STATUS.UPLOADED) {
          setFile(null)
          setIsFileChanged(false)
        }
        setUploadStatus(UPLOAD_STATUS.IDLE)
      }
    },
    [uploadStatus],
  )

  const isUploading = useMemo(
    () => uploadStatus === UPLOAD_STATUS.UPLOADING,
    [uploadStatus],
  )

  const onFileChange = useCallback((newFile: File) => {
    setFile(newFile)
    setIsFileChanged(true)
  }, [])

  const uploadedHandler = useCallback(
    (name: string, dataset: Dataset, uploadToken: string) => {
      setLastUploadedInfo({ name, dataset })
      setUploadedInfo(name, dataset, uploadToken)
      setUploadStatus(UPLOAD_STATUS.UPLOADED)
      setIsFileChanged(false)
      // 次回アップロードのためリセット
      setFile(null)
      fetchDropdownRef.current?.reset()
    },
    [setUploadedInfo],
  )

  const handleUploadClick = async () => {
    if (!file || !presignedUrlInfo) {
      return
    }

    const raw = presignedUrlInfo.dataset
    if (!isDataset(raw)) {
      setErrorMessage(
        'アップロードURLの有効期限が切れました。もう一度、管理ファイル名を選択してURLを取得してください。',
      )
      return
    }
    const dataset: Dataset = raw

    if (file.size > presignedUrlInfo.maxSizeBytes) {
      const mb = Math.ceil(presignedUrlInfo.maxSizeBytes / (1024 * 1024))
      setErrorMessage(`ファイルサイズが上限（${mb}MB）を超えています。`)
      return
    }
    presignedUrlInfo.expiresCallback()
    setErrorMessage(null)
    setUploadStatus(UPLOAD_STATUS.UPLOADING)
    try {
      const response = await uploadCsvToS3(presignedUrlInfo.putUrl, file)
      if (!response) {
        setUploadStatus(UPLOAD_STATUS.IDLE)
        setErrorMessage(
          'アップロードに失敗しました。しばらくしてから再度お試しください。',
        )
        return
      }
      uploadedHandler(file.name, dataset, presignedUrlInfo.uploadToken)
    } catch (error) {
      setUploadStatus(UPLOAD_STATUS.IDLE)
      setErrorMessage(
        `アップロードに失敗しました\n${
          error instanceof Error ? error.message : String(error)
        }`,
      )
    }
  }

  const isFetchedPresignedUrl = useMemo(
    () => !!presignedUrlInfo && uploadStatus !== UPLOAD_STATUS.UPLOADED,
    [presignedUrlInfo, uploadStatus],
  )

  return (
    <div className="flex flex-col gap-4 w-full">
      <span>薬局検索データ更新ツール</span>
      <FetchDropdown
        ref={fetchDropdownRef}
        disabled={isUploading}
        setPresignedUrl={handleSetPresignedUrl}
        triggerType="change"
      />
      <InputFile
        label="CSVファイル"
        file={file}
        onChange={onFileChange}
        accept=".csv"
        multiple={false}
        disabled={isUploading}
      />
      {isFetchedPresignedUrl && (
        <div className="flex flex-col items-center text-sm text-gray-500">
          <p>
            アップロードURLの取得が完了しました。（
            {
              DATASET_OPTIONS.find(
                option => option.value === presignedUrlInfo?.dataset,
              )?.label
            }
            ）
          </p>
          <p>
            {file
              ? `CSVファイルをアップロードしてください。`
              : 'CSVファイルを選択してください。'}
          </p>
        </div>
      )}
      <ButtonPrimary
        onClick={handleUploadClick}
        disabled={!presignedUrlInfo || !file || isUploading || !isFileChanged}
      >
        {uploadStatus === UPLOAD_STATUS.UPLOADING
          ? 'アップロード中'
          : 'アップロード'}
      </ButtonPrimary>
      {errorMessage && <div className="text-red-500">{errorMessage}</div>}
      {lastUploadedInfo && (
        <div className="text-sm text-gray-500 items-center">
          <p>アップロードが完了しました。</p>
          <p>ファイル名: {lastUploadedInfo.name}</p>
          <p>
            データセット:{' '}
            {
              DATASET_OPTIONS.find(
                option => option.value === lastUploadedInfo.dataset,
              )?.label
            }
          </p>
        </div>
      )}
    </div>
  )
}
